import { Box, Link } from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { selectCustomer } from '@jane/monolith/customer/redux/customer';
import { getIdentification } from '@jane/monolith/customer/redux/identification';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { useAuth } from '@jane/monolith/hooks/useAuth/useAuth';
import { LogInModal } from '@jane/shared-ecomm/components';
import { useHeadlessApp } from '@jane/shared/providers';
import { postMessageToIframeParent } from '@jane/shared/util';

interface Props {
  children?: ReactElement;
  isRewards?: boolean;
  rewardsLabel?: string | null;
  specialLabel?: string | null;
}

export const LoginProtected = ({
  children,
  isRewards = false,
  rewardsLabel,
  specialLabel,
}: Props): ReactElement => {
  const { options } = useHeadlessApp();
  const [openModal, setOpenModal] = useState(false);
  const { authenticated } = useCustomerSelector(selectCustomer);
  const dispatch = useCustomerDispatch();

  const {
    defaultLogin,
    errors,
    onAppleClick,
    onGoogleClick,
    register,
    resetError,
    resetPassword,
    showAppleLogin,
    showGoogleLogin,
  } = useAuth();

  const renderedSpecialLabel = `Sign in to see ${capitalize(
    specialLabel || 'discounts'
  )}`;

  const renderedRewardsLabel = `Sign in to see ${rewardsLabel || 'rewards'}`;

  // load/unload the user's document:
  useEffect(() => {
    if (authenticated) {
      dispatch(getIdentification());
    }
  }, [authenticated]);

  const handleClick = () => {
    if (options?.externalAuth && options?.bloomUserExperience) {
      postMessageToIframeParent({
        messageType: 'authEvent',
        payload: {
          name: 'initiateExternalAuth',
          source: 'bag',
        },
      });
    } else {
      setOpenModal(true);
    }
  };

  if (authenticated && children) return children;

  return (
    <Box pt={8} pb={24} pl={2}>
      <Link onClick={() => handleClick()}>
        {isRewards ? renderedRewardsLabel : renderedSpecialLabel}
      </Link>
      {openModal && (
        <LogInModal
          errors={errors}
          location="cart"
          open={true}
          onAppleClick={onAppleClick}
          onGoogleClick={onGoogleClick}
          onRequestClose={() => {
            resetError();
            setOpenModal(false);
          }}
          onSignInSubmit={defaultLogin}
          onCreateAccountSubmit={register}
          onForgotPasswordSubmit={resetPassword}
          showAppleLogin={showAppleLogin}
          showGoogleLogin={showGoogleLogin}
        />
      )}
    </Box>
  );
};
